import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_URL, getAuthHeaders } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ChevronLeft, ChevronRight, Clock, Users as UsersIcon, Calendar, Grid } from "lucide-react";
import { cn } from "../lib/utils";

// Generar horas del día (0-23)
const HOURS = Array.from({ length: 24 }, (_, i) => i);

// Formatear hora en formato 12h (10A, 11A, 12P, 1P, etc.)
const formatHour = (hour) => {
  if (hour === 0) return "12A";
  if (hour < 12) return `${hour}A`;
  if (hour === 12) return "12P";
  return `${hour - 12}P`;
};

// Formatear hora para mostrar en bloque (6a, 11a, 2p, etc.)
const formatTimeBlock = (time24h) => {
  const [hour, min] = time24h.split(":").map(Number);
  const period = hour >= 12 ? "p" : "a";
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}${period}`;
};

export default function GuardScheduleCalendar() {
  const { t } = useTranslation();
  const { user, canManageCompanies } = useAuth();
  const [guards, setGuards] = useState([]);
  const [workSchedules, setWorkSchedules] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("day"); // "day", "week", or "month"

  const canSelectCompany = () => canManageCompanies();

  useEffect(() => {
    fetchGuards();
    fetchWorkSchedules();
    if (canSelectCompany()) {
      fetchCompanies();
    }
  }, [selectedCompany]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/companies`, {
        headers: getAuthHeaders(),
      });
      setCompanies(response.data || []);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const fetchGuards = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCompany !== "all" && canSelectCompany()) {
        params.append("company_id", selectedCompany);
      } else if (!canSelectCompany() && user?.company_id) {
        params.append("company_id", user.company_id);
      }
      params.append("role", "vigilante");

      const response = await axios.get(`${API_URL}/users?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      const guardsOnly = (response.data || []).filter(u => u.role === "vigilante");
      setGuards(guardsOnly);
    } catch (error) {
      console.error("Error fetching guards:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkSchedules = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCompany !== "all" && canSelectCompany()) {
        params.append("company_id", selectedCompany);
      } else if (!canSelectCompany() && user?.company_id) {
        params.append("company_id", user.company_id);
      }

      const response = await axios.get(`${API_URL}/work-schedules?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      setWorkSchedules(response.data || []);
    } catch (error) {
      console.error("Error fetching work schedules:", error);
    }
  };

  // Obtener el horario de un vigilante para una fecha específica
  const getGuardScheduleForDate = (guard, date) => {
    if (!guard.work_schedule_id) return null;
    
    const schedule = workSchedules.find(s => s.id === guard.work_schedule_id);
    if (!schedule?.days || schedule.days.length === 0) return null;

    // Obtener el día de la semana (0 = domingo, 1 = lunes, ..., 6 = sábado)
    const dayOfWeek = date.getDay();
    const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const dayName = dayNames[dayOfWeek];

    const daySchedule = schedule.days.find(d => d.day_of_week === dayName && d.is_active);
    if (!daySchedule) return null;

    return {
      startTime: daySchedule.start_time,
      endTime: daySchedule.end_time,
      scheduleName: schedule.name,
      scheduleColor: schedule.color || "#3B82F6",
    };
  };

  // Obtener el horario de un vigilante para el día seleccionado (compatibilidad)
  const getGuardSchedule = (guard) => {
    return getGuardScheduleForDate(guard, selectedDate);
  };

  // Calcular posición y ancho del bloque de horario
  const getBlockPosition = (startTime, endTime) => {
    const [startHour, startMin] = startTime.split(":").map(Number);
    const [endHour, endMin] = endTime.split(":").map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    const duration = endMinutes - startMinutes;
    
    // Calcular como porcentaje del día completo (1440 minutos)
    const leftPercent = (startMinutes / 1440) * 100;
    const widthPercent = (duration / 1440) * 100;

    return {
      left: `${leftPercent}%`,
      width: `${widthPercent}%`,
    };
  };

  // Calcular horas trabajadas
  const calculateHours = (startTime, endTime) => {
    const [startHour, startMin] = startTime.split(":").map(Number);
    const [endHour, endMin] = endTime.split(":").map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    const duration = endMinutes - startMinutes;
    return (duration / 60).toFixed(1);
  };

  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const goToPreviousWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 7);
    setSelectedDate(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 7);
    setSelectedDate(newDate);
  };

  const goToPreviousMonth = () => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setSelectedDate(newDate);
  };

  const goToNextMonth = () => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setSelectedDate(newDate);
  };

  // Obtener los días de la semana actual
  const getWeekDays = (date) => {
    const weekDays = [];
    const dayOfWeek = date.getDay(); // 0 = domingo, 1 = lunes, etc.
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - dayOfWeek); // Ir al domingo de la semana
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      weekDays.push(day);
    }
    
    return weekDays;
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  // Funciones para la vista mensual
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Días del mes anterior para completar la primera semana
    const prevMonth = new Date(year, month, 0);
    const daysInPrevMonth = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, daysInPrevMonth - i),
        isCurrentMonth: false,
      });
    }
    
    // Días del mes actual
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        date: new Date(year, month, day),
        isCurrentMonth: true,
      });
    }
    
    // Días del mes siguiente para completar la última semana
    const remainingDays = 42 - days.length; // 6 semanas * 7 días
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        date: new Date(year, month + 1, day),
        isCurrentMonth: false,
      });
    }
    
    return days;
  };

  const dateStr = selectedDate.toLocaleDateString(undefined, { 
    weekday: "long", 
    year: "numeric", 
    month: "long", 
    day: "numeric" 
  });

  const monthStr = selectedDate.toLocaleDateString(undefined, { 
    year: "numeric", 
    month: "long" 
  });

  const weekStr = () => {
    const weekDays = getWeekDays(selectedDate);
    const start = weekDays[0];
    const end = weekDays[6];
    const startStr = start.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    const endStr = end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    return `${startStr} - ${endStr}`;
  };

  const isToday = selectedDate.toDateString() === new Date().toDateString();
  
  const isTodayDate = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            {t("pages.guardScheduleCalendar.title")}
          </h1>
          <p className="text-slate-600 mt-1">{t("pages.guardScheduleCalendar.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          {canSelectCompany() && (
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder={t("pages.workSchedules.allCompanies")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("pages.workSchedules.allCompanies")}</SelectItem>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Controles de navegación */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={
                  viewMode === "day" ? goToPreviousDay : 
                  viewMode === "week" ? goToPreviousWeek : 
                  goToPreviousMonth
                }
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday}>
                {t("pages.guardScheduleCalendar.today")}
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={
                  viewMode === "day" ? goToNextDay : 
                  viewMode === "week" ? goToNextWeek : 
                  goToNextMonth
                }
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <span className={cn("ml-4 font-medium", isToday && "text-blue-600")}>
                {viewMode === "day" ? dateStr : viewMode === "week" ? weekStr() : monthStr}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === "day" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("day")}
              >
                <Clock className="h-4 w-4 mr-2" />
                {t("pages.guardScheduleCalendar.dayView")}
              </Button>
              <Button
                variant={viewMode === "week" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("week")}
              >
                <Grid className="h-4 w-4 mr-2" />
                {t("pages.guardScheduleCalendar.weekView")}
              </Button>
              <Button
                variant={viewMode === "month" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("month")}
              >
                <Calendar className="h-4 w-4 mr-2" />
                {t("pages.guardScheduleCalendar.monthView")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendario */}
      {loading ? (
        <Card>
          <CardContent className="p-8 text-center">
            <p>{t("common.loading")}</p>
          </CardContent>
        </Card>
      ) : guards.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <UsersIcon className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">{t("pages.guardScheduleCalendar.noGuards")}</p>
          </CardContent>
        </Card>
      ) : viewMode === "day" ? (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full">
                {/* Header con horas */}
                <div className="grid grid-cols-[220px_1fr] border-b bg-slate-100">
                  <div className="p-4 font-semibold text-slate-700 border-r bg-white">
                    {t("pages.guardScheduleCalendar.employees")}
                  </div>
                  <div className="relative h-16">
                    {/* Etiquetas de horas */}
                    {HOURS.map((hour) => (
                      <div
                        key={hour}
                        className="absolute top-0 bottom-0 flex items-center justify-center border-l border-slate-300"
                        style={{
                          left: `${(hour / 24) * 100}%`,
                          width: `${(1 / 24) * 100}%`,
                        }}
                      >
                        <span className="text-xs font-medium text-slate-600">
                          {formatHour(hour)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Filas de vigilantes */}
                {guards.map((guard, index) => {
                  const schedule = getGuardSchedule(guard);
                  const position = schedule ? getBlockPosition(schedule.startTime, schedule.endTime) : null;

                  return (
                    <div
                      key={guard.id}
                      className={cn(
                        "grid grid-cols-[220px_1fr] border-b last:border-b-0",
                        index % 2 === 0 ? "bg-white" : "bg-slate-50"
                      )}
                    >
                      {/* Columna de vigilante */}
                      <div className="p-4 border-r flex items-center gap-3">
                        {guard.photo_base64 ? (
                          <img
                            src={`data:image/jpeg;base64,${guard.photo_base64}`}
                            alt={guard.name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-slate-200"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center border-2 border-slate-300">
                            <span className="text-slate-600 text-lg font-semibold">
                              {guard.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm text-slate-900 truncate">
                            {guard.name}
                          </div>
                          {schedule && (
                            <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                              <Clock className="h-3 w-3" />
                              <span>{calculateHours(schedule.startTime, schedule.endTime)}h</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Área de horario */}
                      <div className="relative h-20 border-l">
                        {/* Líneas verticales de horas */}
                        {HOURS.map((hour) => (
                          <div
                            key={hour}
                            className="absolute top-0 bottom-0 border-l border-slate-200"
                            style={{
                              left: `${(hour / 24) * 100}%`,
                            }}
                          />
                        ))}

                        {/* Bloque de horario */}
                        {schedule && position && (
                          <div
                            className="absolute top-2 bottom-2 rounded-lg px-3 py-2 text-white text-xs font-semibold shadow-lg flex flex-col justify-center overflow-hidden"
                            style={{
                              backgroundColor: schedule.scheduleColor,
                              left: position.left,
                              width: position.width,
                              minWidth: "120px",
                            }}
                            title={`${schedule.startTime} - ${schedule.endTime}${schedule.scheduleName ? ` (${schedule.scheduleName})` : ""}`}
                          >
                            <div className="whitespace-nowrap font-bold">
                              {formatTimeBlock(schedule.startTime)} - {formatTimeBlock(schedule.endTime)}
                            </div>
                            {schedule.scheduleName && (
                              <div className="text-[10px] opacity-95 mt-0.5 truncate whitespace-nowrap">
                                {schedule.scheduleName}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : viewMode === "week" ? (
        /* Vista Semanal */
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <div className="min-w-full">
                {/* Header con días de la semana */}
                <div className="grid grid-cols-[220px_repeat(7,1fr)] border-b bg-slate-100">
                  <div className="p-4 font-semibold text-slate-700 border-r bg-white">
                    {t("pages.guardScheduleCalendar.employees")}
                  </div>
                  {getWeekDays(selectedDate).map((day, index) => {
                    const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
                    const dayName = dayNames[index];
                    const isToday = isTodayDate(day);
                    
                    return (
                      <div 
                        key={index} 
                        className={cn(
                          "p-3 text-center border-r last:border-r-0",
                          isToday && "bg-blue-100"
                        )}
                      >
                        <div className="text-xs font-semibold text-slate-600 mb-1">
                          {t(`pages.workSchedules.${dayName}`)}
                        </div>
                        <div className={cn(
                          "text-sm font-bold",
                          isToday ? "text-blue-600" : "text-slate-700"
                        )}>
                          {day.getDate()}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Filas de vigilantes */}
                {guards.map((guard, guardIndex) => {
                  return (
                    <div
                      key={guard.id}
                      className={cn(
                        "grid grid-cols-[220px_repeat(7,1fr)] border-b last:border-b-0",
                        guardIndex % 2 === 0 ? "bg-white" : "bg-slate-50"
                      )}
                    >
                      {/* Columna de vigilante */}
                      <div className="p-4 border-r flex items-center gap-3">
                        {guard.photo_base64 ? (
                          <img
                            src={`data:image/jpeg;base64,${guard.photo_base64}`}
                            alt={guard.name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-slate-200"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center border-2 border-slate-300">
                            <span className="text-slate-600 text-lg font-semibold">
                              {guard.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm text-slate-900 truncate">
                            {guard.name}
                          </div>
                        </div>
                      </div>

                      {/* Días de la semana */}
                      {getWeekDays(selectedDate).map((day, dayIndex) => {
                        const schedule = getGuardScheduleForDate(guard, day);
                        const isToday = isTodayDate(day);
                        
                        return (
                          <div
                            key={dayIndex}
                            className={cn(
                              "p-3 border-r last:border-r-0 min-h-[80px]",
                              isToday && "bg-blue-50"
                            )}
                          >
                            {schedule && (
                              <div
                                className="text-[11px] px-2 py-1.5 rounded text-white font-semibold"
                                style={{
                                  backgroundColor: schedule.scheduleColor,
                                }}
                                title={`${schedule.startTime} - ${schedule.endTime}${schedule.scheduleName ? ` (${schedule.scheduleName})` : ""}`}
                              >
                                <div className="font-bold mb-0.5">
                                  {formatTimeBlock(schedule.startTime)} - {formatTimeBlock(schedule.endTime)}
                                </div>
                                {schedule.scheduleName && (
                                  <div className="text-[9px] opacity-95 truncate">
                                    {schedule.scheduleName}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Vista Mensual */
        <div className="space-y-4">
          {/* Leyenda de vigilantes */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                {guards.map((guard, index) => {
                  // Generar un color único para cada vigilante basado en su índice
                  const colors = [
                    "#3B82F6", // Azul
                    "#10B981", // Verde
                    "#F59E0B", // Amarillo/Naranja
                    "#EF4444", // Rojo
                    "#8B5CF6", // Púrpura
                    "#EC4899", // Rosa
                    "#06B6D4", // Cian
                    "#84CC16", // Lima
                    "#F97316", // Naranja
                    "#6366F1", // Índigo
                  ];
                  const guardColor = colors[index % colors.length];
                  
                  return (
                    <div key={guard.id} className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                        style={{ backgroundColor: guardColor }}
                      />
                      <span className="text-sm text-slate-700 font-medium">{guard.name}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Calendario mensual */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <div className="min-w-full">
                  {/* Header con días de la semana */}
                  <div className="grid grid-cols-7 border-b bg-slate-100">
                    {["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"].map((day) => (
                      <div key={day} className="p-3 text-center text-sm font-semibold text-slate-700 border-r last:border-r-0">
                        {t(`pages.workSchedules.${day}`)}
                      </div>
                    ))}
                  </div>

                  {/* Días del mes */}
                  <div className="grid grid-cols-7">
                    {getDaysInMonth(selectedDate).map((dayInfo, index) => {
                      const isToday = isTodayDate(dayInfo.date);
                      
                      // Obtener todos los vigilantes que tienen horario en este día
                      const guardsWithSchedule = guards
                        .map((guard, guardIndex) => {
                          const schedule = getGuardScheduleForDate(guard, dayInfo.date);
                          if (!schedule || !dayInfo.isCurrentMonth) return null;
                          
                          const colors = [
                            "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6",
                            "#EC4899", "#06B6D4", "#84CC16", "#F97316", "#6366F1",
                          ];
                          const guardColor = colors[guardIndex % colors.length];
                          
                          return {
                            guard,
                            schedule,
                            color: guardColor,
                          };
                        })
                        .filter(Boolean);
                      
                      return (
                        <div
                          key={index}
                          className={cn(
                            "p-3 border-r border-b min-h-[100px]",
                            !dayInfo.isCurrentMonth && "bg-slate-50 opacity-50",
                            isToday && "bg-blue-50 border-blue-200"
                          )}
                        >
                          <div className={cn(
                            "text-sm font-medium mb-2",
                            !dayInfo.isCurrentMonth ? "text-slate-400" : "text-slate-700",
                            isToday && "text-blue-600 font-bold"
                          )}>
                            {dayInfo.date.getDate()}
                          </div>
                          
                          {/* Indicadores de vigilantes */}
                          {dayInfo.isCurrentMonth && guardsWithSchedule.length > 0 && (
                            <div className="space-y-1">
                              {guardsWithSchedule.map(({ guard, schedule, color }) => (
                                <div
                                  key={guard.id}
                                  className="flex items-center gap-1.5 text-[10px]"
                                  title={`${guard.name}: ${schedule.startTime} - ${schedule.endTime}${schedule.scheduleName ? ` (${schedule.scheduleName})` : ""}`}
                                >
                                  <div
                                    className="w-2.5 h-2.5 rounded-full border border-white shadow-sm flex-shrink-0"
                                    style={{ backgroundColor: color }}
                                  />
                                  <span className="text-slate-600 truncate font-medium">
                                    {formatTimeBlock(schedule.startTime)}-{formatTimeBlock(schedule.endTime)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
